<template>
  <p class="dialogButtons">
    <button
      v-for="button in orderedButtons"
      class="big"
      :class="button.class"
      :key="createButtonChecksum(button)"
      @click="button.callback"
    >
      <LoadingAnimation v-if="button.loading" />
      <FaIcon
        v-else-if="button.icon"
        :icon="button.icon"
        :class="button.iconClass"
      />
      {{ button.text }}
    </button>
  </p>
</template>

<script>
import FaIcon from './ui/FaIcon'
import LoadingAnimation from './LoadingAnimation'

/**
 * @typedef {{
 *  text: string,
 *  callback: (function(Event)),
 *  loading: (boolean|undefined),
 *  silent: (boolean|undefined),
 *  icon: (string|undefined),
 *  iconClass: (string|undefined),
 *  class: (string|undefined),
 *  focus: (boolean|undefined),
 *  lineBreakAfter: (boolean|undefined)
 * }}
 */
let ButtonData // eslint-disable-line no-unused-vars

/**
 * This is a direct Vue clone of the edulab.ui.DialogButtons component for Google Closure.
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */
export default {
  components: { FaIcon, LoadingAnimation },
  props: {
    buttons: {
      type: Array,
      required: true,
      validator(buttons) {
        return !buttons.some(button => {
          return !(
            (button.text || button.icon) &&
            typeof button.callback === 'function'
          )
        })
      },
    },
  },
  data() {
    return {
      isOnWindows:
        window &&
        window.navigator &&
        /^win.*/.test(window.navigator.platform.toLowerCase()),
    }
  },
  computed: {
    /**
     * Returns the buttons array ordered according to the standards for the current operating system.
     * Windows wants the buttons in the opposite order of every other OS, so we'll just reverse the array.
     * @returns {!Array<!ButtonData>}
     */
    orderedButtons() {
      if (this.isOnWindows) {
        // Clone and reverse the buttons array
        // We would really like to clone by using `[...this.buttons]`but that technique breaks our test
        // because of a jest/vue/babel issue that we can't figure out how to solve at the moment.
        // (it throws "SyntaxError: Unexpected identifier" at a seemingly random line in the same file)
        return this.buttons.map(button => button).reverse()
      }

      return this.buttons
    },
  },
  methods: {
    /**
     * Returns a checksum for the given button.
     * Since we should never present the user with more than 1 button with the same text, icon and class it should be a valid checksum.
     * @param {ButtonData} button
     * @returns {string}
     */
    createButtonChecksum(button) {
      return `${button.text}${button.icon}${button.class}`
    },
  },
}
</script>

<style lang="scss" scoped>
.dialogButtons > button {
  border-radius: 0;

  &:focus {
    background: $white;
  }
}
</style>
